import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { AlertTriangle, Download, Pencil, Mail, Send, Loader2 } from "lucide-react";

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const COMMON_DOMAIN_TYPOS: Record<string, string> = {
  "gmail,com": "gmail.com",
  "gnail.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.co": "gmail.com",
  "yahooo.com": "yahoo.com",
  "yaho.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "hotmial.com": "hotmail.com",
  "hotmaill.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
};

type Source = "pre_registration" | "student";
type Issue = "missing" | "invalid_format" | "comma" | "space" | "double_dot" | "trailing_dot" | "domain_typo" | "duplicate";

interface FlaggedRow {
  source: Source;
  id: string;
  user_id: string | null;
  full_name: string;
  form_iv_index: string | null;
  email: string | null;
  issues: Issue[];
  suggestion: string | null;
  program: string | null;
  klass: string | null;
}

function detect(email: string | null): { issues: Issue[]; suggestion: string | null } {
  if (!email || !email.trim()) return { issues: ["missing"], suggestion: null };
  const e = email.trim();
  const issues: Issue[] = [];
  let suggestion: string | null = null;

  if (e.includes(",")) issues.push("comma");
  if (/\s/.test(e)) issues.push("space");
  if (e.includes("..")) issues.push("double_dot");
  if (e.endsWith(".")) issues.push("trailing_dot");

  // Domain typo
  const at = e.lastIndexOf("@");
  if (at > 0) {
    const domain = e.slice(at + 1).toLowerCase();
    const fixed = COMMON_DOMAIN_TYPOS[domain];
    if (fixed) {
      issues.push("domain_typo");
      suggestion = e.slice(0, at + 1) + fixed;
    }
  }

  if (!EMAIL_RE.test(e) && !issues.length) issues.push("invalid_format");
  else if (!EMAIL_RE.test(e) && !issues.includes("invalid_format")) {
    // already flagged by specific issue, but still invalid overall
  }

  // Build a generic suggestion if comma present and no domain-typo suggestion
  if (!suggestion && issues.includes("comma")) {
    const guess = e.replace(/,/g, ".");
    if (EMAIL_RE.test(guess)) suggestion = guess;
  }
  if (!suggestion && issues.includes("space")) {
    const guess = e.replace(/\s+/g, "");
    if (EMAIL_RE.test(guess)) suggestion = guess;
  }

  return { issues, suggestion };
}

const ISSUE_LABELS: Record<Issue, string> = {
  missing: "Missing email",
  invalid_format: "Invalid format",
  comma: "Comma in address",
  space: "Whitespace",
  double_dot: "Double dot",
  trailing_dot: "Trailing dot",
  domain_typo: "Domain typo",
  duplicate: "Duplicate email",
};

const ISSUE_VARIANT: Record<Issue, "destructive" | "secondary" | "outline"> = {
  missing: "destructive",
  invalid_format: "destructive",
  comma: "destructive",
  space: "destructive",
  double_dot: "secondary",
  trailing_dot: "secondary",
  domain_typo: "secondary",
  duplicate: "outline",
};

export default function EmailAudit() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"all" | Source>("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<FlaggedRow | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [resending, setResending] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["email-audit"],
    queryFn: async () => {
      const [preRes, stRes] = await Promise.all([
        supabase
          .from("student_pre_registrations")
          .select("id, full_name, form_iv_index, email, program, class")
          .order("full_name", { ascending: true }),
        supabase
          .from("students")
          .select("id, user_id, first_name, last_name, form_iv_index, email, program, class")
          .order("first_name", { ascending: true }),
      ]);
      if (preRes.error) throw preRes.error;
      if (stRes.error) throw stRes.error;

      const rows: FlaggedRow[] = [];

      // Build duplicate map across both sources (case-insensitive)
      const counts = new Map<string, number>();
      const bump = (e: string | null) => {
        if (!e) return;
        const key = e.trim().toLowerCase();
        if (!key || !EMAIL_RE.test(key)) return;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      };
      preRes.data?.forEach((r) => bump(r.email));
      stRes.data?.forEach((r) => bump(r.email));

      preRes.data?.forEach((r) => {
        const { issues, suggestion } = detect(r.email);
        if (r.email && counts.get(r.email.trim().toLowerCase())! > 1) issues.push("duplicate");
        if (issues.length) {
          rows.push({
            source: "pre_registration",
            id: r.id,
            user_id: null,
            full_name: r.full_name ?? "—",
            form_iv_index: r.form_iv_index,
            email: r.email,
            issues,
            suggestion,
            program: r.program,
            klass: r.class,
          });
        }
      });

      stRes.data?.forEach((r) => {
        const { issues, suggestion } = detect(r.email);
        if (r.email && counts.get(r.email.trim().toLowerCase())! > 1) issues.push("duplicate");
        if (issues.length) {
          rows.push({
            source: "student",
            id: r.id,
            user_id: r.user_id,
            full_name: `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() || "—",
            form_iv_index: r.form_iv_index,
            email: r.email,
            issues,
            suggestion,
            program: r.program,
            klass: r.class,
          });
        }
      });

      return rows;
    },
  });

  const filtered = useMemo(() => {
    let rows = data ?? [];
    if (tab !== "all") rows = rows.filter((r) => r.source === tab);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.full_name.toLowerCase().includes(q) ||
          (r.form_iv_index ?? "").toLowerCase().includes(q) ||
          (r.email ?? "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, tab, search]);

  const stats = useMemo(() => {
    const all = data ?? [];
    return {
      total: all.length,
      pre: all.filter((r) => r.source === "pre_registration").length,
      st: all.filter((r) => r.source === "student").length,
      missing: all.filter((r) => r.issues.includes("missing")).length,
      invalid: all.filter((r) => r.issues.some((i) => i !== "missing" && i !== "duplicate")).length,
      duplicates: all.filter((r) => r.issues.includes("duplicate")).length,
    };
  }, [data]);

  const saveEmail = useMutation({
    mutationFn: async (args: { row: FlaggedRow; email: string }) => {
      const e = args.email.trim().toLowerCase();
      if (!EMAIL_RE.test(e)) throw new Error("Invalid email format");

      // Sync auth + students via edge function (also updates students.email)
      if (args.row.source === "student" && args.row.user_id) {
        const { data: result, error } = await supabase.functions.invoke("admin-change-user-email", {
          body: { user_id: args.row.user_id, new_email: e },
        });
        if (error) throw error;
        if (result?.error) throw new Error(result.error);
      } else if (args.row.source === "student") {
        // No user_id — just update the students table
        const { error } = await supabase.from("students").update({ email: e }).eq("id", args.row.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("student_pre_registrations")
          .update({ email: e })
          .eq("id", args.row.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: "Email updated" });
      setEditing(null);
      setNewEmail("");
      qc.invalidateQueries({ queryKey: ["email-audit"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to update", description: err.message, variant: "destructive" });
    },
  });

  async function resendReset(row: FlaggedRow) {
    if (!row.email || !EMAIL_RE.test(row.email)) {
      toast({ title: "Cannot send", description: "Fix the email first.", variant: "destructive" });
      return;
    }
    setResending(row.id);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(row.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast({ title: "Reset email sent", description: row.email });
    } catch (err: any) {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    } finally {
      setResending(null);
    }
  }

  function downloadCsv() {
    const rows = filtered;
    const header = ["source", "name", "form_iv_index", "email", "issues", "suggestion", "class", "program"];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      const cells = [
        r.source,
        r.full_name,
        r.form_iv_index ?? "",
        r.email ?? "",
        r.issues.map((i) => ISSUE_LABELS[i]).join("; "),
        r.suggestion ?? "",
        r.klass ?? "",
        r.program ?? "",
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `email-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function openEdit(row: FlaggedRow) {
    setEditing(row);
    setNewEmail(row.suggestion ?? row.email ?? "");
  }

  return (
    <AppLayout>
      <div className="space-y-4">
        <div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            Email Audit & Repair
          </h1>
          <p className="text-muted-foreground text-sm">
            Find and fix invalid student emails so password reset and notification messages are delivered.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <StatCard label="Flagged total" value={stats.total} />
          <StatCard label="Pre-registrations" value={stats.pre} />
          <StatCard label="Onboarded" value={stats.st} />
          <StatCard label="Missing" value={stats.missing} tone="destructive" />
          <StatCard label="Invalid / typo" value={stats.invalid} tone="destructive" />
          <StatCard label="Duplicates" value={stats.duplicates} tone="warning" />
        </div>

        <Card>
          <CardHeader className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-base">Flagged students</CardTitle>
                <CardDescription>{filtered.length} of {data?.length ?? 0} shown</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!filtered.length}>
                <Download className="h-4 w-4 mr-2" /> Download CSV
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="pre_registration">Pre-registrations</TabsTrigger>
                  <TabsTrigger value="student">Onboarded</TabsTrigger>
                </TabsList>
              </Tabs>
              <Input
                placeholder="Search by name, Form IV index or email"
                className="max-w-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className="table-responsive">
            {isLoading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Scanning emails…</div>
            ) : !filtered.length ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No flagged emails. All addresses look deliverable.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Source</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Form IV Index</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Issue</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={`${r.source}-${r.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {r.source === "pre_registration" ? "Pre-reg" : "Onboarded"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{r.full_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.form_iv_index ?? "—"}</TableCell>
                      <TableCell className="text-xs">{r.klass ?? "—"}</TableCell>
                      <TableCell className="text-xs break-all">
                        <div>{r.email || <span className="text-muted-foreground italic">—</span>}</div>
                        {r.suggestion && (
                          <div className="text-xs text-primary mt-1">Suggested: {r.suggestion}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {r.issues.map((i) => (
                            <Badge key={i} variant={ISSUE_VARIANT[i]} className="text-xs">
                              {ISSUE_LABELS[i]}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)}>
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Fix
                          </Button>
                          {r.source === "student" && r.user_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={resending === r.id || !r.email || !EMAIL_RE.test(r.email)}
                              onClick={() => resendReset(r)}
                              title={!r.email || !EMAIL_RE.test(r.email) ? "Fix email first" : "Resend password reset"}
                            >
                              {resending === r.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Send className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-4 w-4" /> Correct email
              </DialogTitle>
              <DialogDescription>
                {editing?.full_name} · {editing?.form_iv_index ?? "no Form IV"}
                {editing?.source === "student" && editing.user_id
                  ? " · Updates login email so future password resets work."
                  : " · Updates pre-registration record only."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Current</Label>
                <div className="text-sm font-mono break-all">{editing?.email || "—"}</div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-email">New email</Label>
                <Input
                  id="new-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="student@example.com"
                />
                {editing?.suggestion && newEmail !== editing.suggestion && (
                  <button
                    type="button"
                    className="text-xs text-primary underline"
                    onClick={() => setNewEmail(editing.suggestion!)}
                  >
                    Use suggestion: {editing.suggestion}
                  </button>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                onClick={() => editing && saveEmail.mutate({ row: editing, email: newEmail })}
                disabled={saveEmail.isPending || !EMAIL_RE.test(newEmail.trim().toLowerCase())}
              >
                {saveEmail.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "destructive" | "warning" }) {
  const color =
    tone === "destructive" ? "text-destructive" : tone === "warning" ? "text-amber-600" : "text-foreground";
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
